import { useState } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface ImageItem {
  url: string;
  order: number;
}

interface ImageCarouselProps {
  images: ImageItem[];
  alt: string;
  className?: string;
}

export const ImageCarousel = ({ images, alt, className }: ImageCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!images || images.length === 0) return null;

  const sortedImages = [...images].sort((a, b) => a.order - b.order);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : sortedImages.length - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < sortedImages.length - 1 ? prev + 1 : 0));
  };

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <div className="relative rounded-lg overflow-hidden bg-muted">
        <img
          src={sortedImages[currentIndex].url}
          alt={`${alt} ${currentIndex + 1}`}
          className="w-full h-auto max-h-[300px] object-contain"
        />
        {sortedImages.length > 1 && (
          <div className="absolute bottom-2 right-2 flex gap-1">
            <Button
              size="icon"
              variant="secondary"
              className="h-8 w-8"
              onClick={handlePrevious}
            >
              <ChevronUp className="h-4 w-4" />
            </Button>
            <Button
              size="icon"
              variant="secondary"
              className="h-8 w-8"
              onClick={handleNext}
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
      {sortedImages.length > 1 && (
        <div className="flex gap-1 justify-center">
          {sortedImages.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={cn(
                "h-2 rounded-full transition-all",
                idx === currentIndex ? "w-8 bg-primary" : "w-2 bg-muted-foreground/30"
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
};
